import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { LogOut, Users, Award, Mail, Settings } from 'lucide-react';
import CreatorsAdmin from '../components/admin/CreatorsAdmin';
import BrandsAdmin from '../components/admin/BrandsAdmin';
import SubmissionsAdmin from '../components/admin/SubmissionsAdmin';
import SettingsAdmin from '../components/admin/SettingsAdmin';
import { toast } from 'sonner';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-orange-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src="https://customer-assets.emergentagent.com/job_d17b1037-2ee5-4a1e-b027-553c006871ab/artifacts/susrxk43_creator_first_logo.png"
              alt="Creator First"
              className="h-10 w-auto"
            />
            <div className="hidden sm:block border-l border-gray-300 pl-4">
              <h1 className="text-xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-xs text-gray-500">Manage your content</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex flex-col items-end">
              <span className="text-sm font-bold text-gray-900" data-testid="admin-user-name">
                {user?.name || 'Admin'}
              </span>
              <span className="text-xs text-gray-500">{user?.email}</span>
            </div>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="border-orange-300 text-orange-600 hover:bg-orange-50"
              data-testid="admin-view-site-button"
            >
              View Site
            </Button>
            <Button
              onClick={handleLogout}
              className="bg-gradient-to-r from-rose-600 to-orange-600 hover:from-rose-700 hover:to-orange-700 text-white"
              data-testid="admin-logout-button"
            >
              <LogOut size={18} className="mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="creators" className="w-full">
          <TabsList className="grid grid-cols-4 max-w-3xl mb-8 bg-white border border-gray-200 p-1 h-auto">
            <TabsTrigger
              value="creators"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-rose-500 data-[state=active]:text-white py-3 font-semibold"
              data-testid="tab-creators"
            >
              <Users size={18} className="mr-2" />
              Creators
            </TabsTrigger>
            <TabsTrigger
              value="brands"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-violet-600 data-[state=active]:text-white py-3 font-semibold"
              data-testid="tab-brands"
            >
              <Award size={18} className="mr-2" />
              Brands
            </TabsTrigger>
            <TabsTrigger
              value="submissions"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-600 data-[state=active]:to-teal-600 data-[state=active]:text-white py-3 font-semibold"
              data-testid="tab-submissions"
            >
              <Mail size={18} className="mr-2" />
              Messages
            </TabsTrigger>
            <TabsTrigger
              value="settings"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-600 data-[state=active]:to-orange-600 data-[state=active]:text-white py-3 font-semibold"
              data-testid="tab-settings"
            >
              <Settings size={18} className="mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="creators">
            <CreatorsAdmin />
          </TabsContent>
          <TabsContent value="brands">
            <BrandsAdmin />
          </TabsContent>
          <TabsContent value="submissions">
            <SubmissionsAdmin />
          </TabsContent>
          <TabsContent value="settings">
            <SettingsAdmin />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
